import RespImage from '@/app/_components/core/resp-image';
import { SmartLink } from '@/app/_components/core/smart-link';
import type { EventCardItem } from '@/app/_components/shared/event-card/event-card.types';
import type { CardVariant } from '@/app/_components/shared/event-card/event-card.variants';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import { emptyToUndefined } from '@/app/_lib/utils';

/**
 * Resolve image data with art direction based on card variant.
 * Large/grid variants use portrait on desktop, default uses landscape.
 */
function getImageData(
  candidates: NonNullable<EventCardItem['image']>['srcsetCandidates'],
  variant: CardVariant
) {
  if (Array.isArray(candidates)) {
    return transformSrcsetCandidates(candidates);
  }

  // No cast: after the Array.isArray guard this is already the (partial)
  // art-direction record from the schema — the old `as Record<...>` claimed
  // every orientation key exists and made the fallbacks below look dead.
  const recordCandidates = candidates;

  if (variant === 'large' || variant === 'grid') {
    const square = recordCandidates.square ?? recordCandidates.landscape;
    const portrait = recordCandidates.portrait ?? recordCandidates.landscape;

    if (square && portrait) {
      return transformSrcsetCandidates({
        fallback: square,
        variants: [
          { media: '(min-width: 1024px)', srcsetCandidates: portrait },
        ],
      });
    }
  }

  return transformSrcsetCandidates(recordCandidates.landscape ?? []);
}

export interface CardImageProps {
  event: EventCardItem;
  variant: CardVariant;
  sizes: string;
  className?: string;
  trackingPrefix?: string;
}

export function CardImage({
  event,
  variant,
  sizes,
  className,
  trackingPrefix,
}: CardImageProps) {
  if (!event.image) return null;

  const imageData = getImageData(event.image.srcsetCandidates, variant);

  const image = (
    <RespImage
      srcsetCandidates={imageData.srcsetCandidates}
      artDirection={imageData.artDirection}
      alt={emptyToUndefined(event.image.altText) ?? event.title}
      title={emptyToUndefined(event.image.title)}
      className="size-full  object-cover transition-transform duration-300 [@media(hover:hover)]:hover:scale-105"
      sizes={sizes}
    />
  );

  if (!event.detailLink) {
    return <div className={className}>{image}</div>;
  }

  return (
    <SmartLink
      href={event.detailLink}
      className={className}
      data-track-id={trackingPrefix ? `${trackingPrefix}-image` : undefined}
    >
      {image}
    </SmartLink>
  );
}
